/**
 * <park-booking-calendar> — presentational free/busy calendar SHELL
 * (epic #138, issue #146). Isomorphic: pure renderParkBookingCalendar() shared
 * by ds/ParkBookingCalendar.astro and the custom element (see
 * docs/design-system-rebuild-plan.md).
 *
 * IMPORTANT — this is a SHELL only. varaa.astro's inline script owns the live
 * behaviour: it queries the exact ids emitted here (#cal-prev/#cal-title/
 * #cal-next/#cal-strip/#cal-grid/#cal-weekdays/#cal-nextfree/#cal-loading) and
 * the `cal-view` radio group's `name` attribute, and injects the day cells
 * (.cal-cell*) + hour chips at runtime. Therefore observedAttributes=[] — the
 * element must NEVER re-render after first paint or it would wipe the
 * script-populated grid. The cell/chip/legend CSS lives in
 * wc/css/park-booking-calendar.css so the JS-generated nodes are styled.
 *
 * Attributes (all pre-resolved strings from the page locale):
 *   title, prev-label, next-label, loading-text, legend-unavailable,
 *   next-free-label, day-view-label, month-view-label, cal-view-legend.
 *
 * THE VIEW SWITCHER (anton/forrest#1392) is a nested `<park-choice
 * variant="segmented">` with two options ("days" checked by default, the
 * compact strip; "month"), composed HERE via a direct call to
 * `renderParkChoice()` rather than emitted as bare markup — this is the one
 * place in the library where one element's pure render fn calls another's;
 * it keeps the switcher correct in EVERY render path (SSR wrapper, bare
 * custom element, no-JS gate) with no extra plumbing in any of them, instead
 * of duplicating the composition at each call site. It replaces a single
 * `<button aria-expanded>` that toggled `.hidden` on the strip/grid via a
 * text-swapping label — that button's own CSS carried a bug (anton/forrest#1392:
 * an author `display` rule beat the UA `[hidden]` rule, so BOTH views
 * rendered at once) fixed below regardless of the switcher's shape, since
 * `setMonthOpen()` still drives the same `.hidden` toggle.
 *
 * @dsSpec booking-flow-ux#FR-17 — the availability calendar a visitor picks a slot from.
 *   (Traceability: specs/design-system/traceability.md; the generated card
 *   carries this same ref in its @dsCard marker — anton/forrest#846.)
 */

import { ParkElement, defineElement, esc, html } from "./base";
import { renderParkChoice } from "./park-choice";

const TAG = "park-booking-calendar";

export interface ParkBookingCalendarProps {
	title?: string;
	prevLabel?: string;
	nextLabel?: string;
	loadingText?: string;
	/** RETIRED (anton/forrest#1447) — the swatch legend is gone; the day's own
	 *  `aria-disabled` + state label carries the meaning. The prop is still
	 *  accepted so an existing `legend-unavailable` attribute in the wild is a
	 *  no-op rather than a hard error; it renders nothing. */
	legendUnavailable?: string;
	/** FR-13b — the escape hatch from blind paging. */
	nextFreeLabel?: string;
	/** FR-13f — the compact-strip segment's label (checked by default). */
	dayViewLabel?: string;
	/** FR-13f — the full-month segment's label. */
	monthViewLabel?: string;
	/** Visually-hidden accessible name for the two-segment view switcher. */
	calViewLegend?: string;
}

/** Pure render: props → the static calendar shell HTML (ids intact). No DOM access.
 *  a11y (anton/forrest#318): the outer `.cal` deliberately carries NO aria-live —
 *  it wraps the whole day strip + nav buttons, so a blanket live region would
 *  make AT re-announce the entire strip on every slice nav / initial paint.
 *  `#cal-loading` carries the loading state; `#bk-summary` (varaa.astro) is the
 *  live region for price/time updates; per-cell `aria-label` (added by the page
 *  script) carries available/unavailable/selected state to AT (not color-only).
 *
 *  fix-stage-island-ux (booking-flow-ux FR-13, revision below spec.md's
 *  preamble): the calendar collapsed from three availability states
 *  (free/partial/full) to two (available/unavailable) — a day the customer
 *  could book at all now reads identically regardless of how much of it is
 *  taken, in exchange for a simpler, glyph-free chip. The legend shrinks to
 *  the one state that still needs explaining: unavailable is the exception,
 *  available is the default and needs no swatch. */
export function renderParkBookingCalendar(
	props: ParkBookingCalendarProps,
): string {
	// a11y (anton/forrest#339, #340): `.cal-body` carries `role="grid"` and owns
	// `#cal-strip` as its single `role="row"` — a `role="row"` outside the grid
	// element is an orphan (axe aria-required-parent, flagged in PR #344
	// review). The page script fills `#cal-strip` with one `role="gridcell"`
	// button per day and drives roving-tabindex + arrow-key navigation.
	// `aria-labelledby="cal-title"` names the grid by the visible date range
	// (the script overwrites `#cal-title` with it).
	//
	// ONE ROW, NOT SIX (FR-13a). The month grid this replaced needed a weekday
	// header row and a 6×7 rowgroup; a strip is a single row of consecutive
	// days, and each day carries its own weekday label, so neither survives.
	//
	// FR-13f — the switcher IS the "current view" indicator (it's a radio
	// group: which segment is checked already says which view is showing), so
	// unlike the button it replaces, nothing here needs to change on toggle —
	// no aria-expanded, no swapped label text.
	const viewSwitcher = renderParkChoice(
		{ name: "cal-view", legend: props.calViewLegend, variant: "segmented" },
		[
			{ value: "days", label: props.dayViewLabel, checked: true },
			{ value: "month", label: props.monthViewLabel },
		],
	);
	return html`<div class="cal"><div class="cal-head"><button type="button" id="cal-prev" class="cal-nav" aria-label="${esc(props.prevLabel)}">‹</button><h2 id="cal-title" class="cal-title">${esc(props.title)}</h2><button type="button" id="cal-next" class="cal-nav" aria-label="${esc(props.nextLabel)}">›</button></div><div class="cal-body" role="grid" aria-labelledby="cal-title"><div id="cal-strip" class="cal-strip" role="row"></div><div id="cal-weekdays" class="cal-weekdays" role="row" hidden></div><div id="cal-grid" class="cal-grid" role="rowgroup" hidden></div></div><div class="cal-tools"><button type="button" id="cal-nextfree" class="cal-nextfree" hidden>${esc(props.nextFreeLabel)}</button><park-choice data-ssr name="cal-view" legend="${esc(props.calViewLegend)}" variant="segmented">${viewSwitcher}</park-choice></div><p id="cal-loading" class="bk-muted" hidden>${esc(props.loadingText)}</p></div>`;
}

class ParkBookingCalendar extends ParkElement {
	static get observedAttributes(): string[] {
		return [];
	}

	protected render(): string {
		return renderParkBookingCalendar({
			title: this.attr("title"),
			prevLabel: this.attr("prev-label"),
			nextLabel: this.attr("next-label"),
			loadingText: this.attr("loading-text"),
			legendUnavailable: this.attr("legend-unavailable"),
			nextFreeLabel: this.attr("next-free-label"),
			dayViewLabel: this.attr("day-view-label"),
			monthViewLabel: this.attr("month-view-label"),
			calViewLegend: this.attr("cal-view-legend"),
		});
	}
}

defineElement(TAG, ParkBookingCalendar);

export { ParkBookingCalendar };
